import { CSSProperties, ReactNode } from 'react';

type Tone = 'sky' | 'dusk' | 'dock' | 'night' | 'depot';
type Rig = 'hotshot' | 'dually-flatbed' | 'gooseneck' | 'enclosed' | 'stepdeck';

interface TruckPhotoProps {
  subject?: string;
  framing?: string;
  tone?: Tone;
  rig?: Rig;
  ratio?: string;
  rounded?: number;
  label?: boolean;
  badge?: ReactNode;
  style?: CSSProperties;
}

const tones: Record<Tone, { sky: string; ground: string; truck: string; accent: string }> = {
  sky:   { sky: 'linear-gradient(180deg, #c9d6df 0%, #e5dfd2 60%, #d8c8a9 100%)', ground: '#3a352c', truck: '#1a1a1a', accent: '#b8351e' },
  dusk:  { sky: 'linear-gradient(180deg, #4a3a3a 0%, #8a5a3a 55%, #c47a4a 100%)', ground: '#1a1612', truck: '#0e0e0e', accent: '#e8a04b' },
  dock:  { sky: 'linear-gradient(180deg, #2a2a2a 0%, #3a3530 100%)',               ground: '#0e0c0a', truck: '#141414', accent: '#d4a24c' },
  night: { sky: 'linear-gradient(180deg, #0a1220 0%, #1a2a3a 100%)',               ground: '#050810', truck: '#0a0a0a', accent: '#b8351e' },
  depot: { sky: 'linear-gradient(180deg, #e5dfd2 0%, #c9c0ad 100%)',               ground: '#5a5040', truck: '#1f1f1f', accent: '#0f2a1d' },
};

export function TruckPhoto({
  subject = 'Hotshot rig — gooseneck loaded',
  framing = '3/4 hero, golden hour, low angle',
  tone = 'sky',
  rig = 'hotshot',
  ratio = '16 / 9',
  rounded = 0,
  label = true,
  badge,
  style = {},
}: TruckPhotoProps) {
  const t = tones[tone];
  return (
    <div style={{
      position: 'relative', aspectRatio: ratio, width: '100%', overflow: 'hidden',
      borderRadius: rounded, background: t.sky, isolation: 'isolate', ...style,
    }}>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '32%', background: t.ground }} />
      <svg viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.18 }}>
        <rect x="20"  y="120" width="60" height="32" fill="#000" />
        <rect x="90"  y="108" width="42" height="44" fill="#000" />
        <rect x="140" y="130" width="80" height="22" fill="#000" />
        <rect x="240" y="115" width="50" height="37" fill="#000" />
        <rect x="300" y="125" width="80" height="27" fill="#000" />
      </svg>
      <TruckSilhouette rig={rig} fill={t.truck} accent={t.accent} />
      <div style={{
        position: 'absolute', inset: 0, mixBlendMode: 'overlay', opacity: 0.35,
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 .6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
      }} />
      {label && (
        <div style={{
          position: 'absolute', top: 10, left: 10, padding: '5px 8px',
          background: 'rgba(245,242,236,0.92)', color: '#1a1a1a',
          fontFamily: 'var(--font-mono)',
          fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase',
          border: '1px solid rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontWeight: 600 }}>Photo · {subject}</div>
          <div style={{ opacity: 0.6, marginTop: 2 }}>{framing}</div>
        </div>
      )}
      {badge && (
        <div style={{ position: 'absolute', bottom: 10, right: 10 }}>{badge}</div>
      )}
    </div>
  );
}

function TruckSilhouette({ rig, fill, accent }: { rig: Rig; fill: string; accent: string }) {
  const common = { fill, stroke: 'none' };
  if (rig === 'hotshot' || rig === 'dually-flatbed') {
    return (
      <svg viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <rect x="200" y="138" width="170" height="9" {...common} />
        <path d="M195 138 L210 138 L210 122 L218 122 L218 138 Z" {...common} />
        {[215, 245, 275, 305, 335, 360].map((x) => <rect key={x} x={x} y="132" width="3" height="6" {...common} />)}
        <path d="M70 138 L80 110 L150 110 L162 138 Z" {...common} />
        <rect x="155" y="118" width="38" height="20" {...common} />
        <rect x="60" y="138" width="135" height="6" {...common} />
        <path d="M88 116 L92 122 L120 122 L120 116 Z" fill="#cfd6db" opacity="0.85" />
        <path d="M124 116 L124 122 L148 122 L142 116 Z" fill="#cfd6db" opacity="0.85" />
        {[88, 110, 175, 235, 280, 325, 360].map((x) => (
          <g key={x}><circle cx={x} cy="150" r="9" fill="#000" /><circle cx={x} cy="150" r="4" fill="#2a2a2a" /></g>
        ))}
        <ellipse cx="55" cy="135" rx="14" ry="3" fill={accent} opacity="0.55" />
      </svg>
    );
  }
  if (rig === 'stepdeck') {
    return (
      <svg viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <rect x="195" y="128" width="40" height="9" {...common} />
        <rect x="235" y="142" width="140" height="6" {...common} />
        <path d="M70 148 L80 120 L150 120 L162 148 Z" {...common} />
        <rect x="155" y="128" width="38" height="20" {...common} />
        {[88, 110, 200, 260, 310, 355].map((x) => (
          <g key={x}><circle cx={x} cy="160" r="9" fill="#000" /></g>
        ))}
      </svg>
    );
  }
  if (rig === 'enclosed') {
    return (
      <svg viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <rect x="195" y="100" width="180" height="48" {...common} />
        <rect x="370" y="100" width="6" height="48" fill="#000" opacity="0.5" />
        <path d="M195 148 L210 148 L210 130 L220 130 L220 148 Z" {...common} />
        <rect x="195" y="148" width="180" height="4" {...common} />
        <path d="M70 148 L80 120 L150 120 L162 148 Z" {...common} />
        <rect x="155" y="128" width="38" height="20" {...common} />
        <rect x="60" y="148" width="135" height="6" {...common} />
        {[88, 110, 175, 235, 290, 345].map((x) => (
          <g key={x}><circle cx={x} cy="160" r="9" fill="#000" /></g>
        ))}
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <rect x="190" y="140" width="190" height="8" {...common} />
      <path d="M70 148 L80 120 L150 120 L162 148 Z" {...common} />
      <rect x="155" y="128" width="38" height="20" {...common} />
      {[88, 110, 200, 260, 320, 365].map((x) => (
        <g key={x}><circle cx={x} cy="160" r="9" fill="#000" /></g>
      ))}
    </svg>
  );
}

const CITY_COORDS: Record<string, [number, number]> = {
  'Fort Worth': [620, 360],
  'Dallas': [640, 360],
  'Houston': [675, 410],
  'Atlanta': [770, 365],
  'Memphis': [710, 335],
  'Chicago': [720, 230],
  'Detroit': [785, 215],
  'Nashville': [745, 320],
  'Charlotte': [830, 320],
  'Phoenix': [310, 380],
  'Denver': [445, 280],
  'Salt Lake City': [365, 235],
  'Los Angeles': [200, 360],
  'Oakland': [165, 290],
  'Seattle': [220, 110],
  'Kansas City': [610, 285],
  'Oklahoma City': [600, 340],
  'Indianapolis': [735, 270],
  'St. Louis': [675, 290],
  'Miami': [840, 470],
  'New Orleans': [685, 440],
  'New York': [905, 230],
};

interface USMapProps {
  stroke?: string;
  fill?: string;
  dots?: string[];
  lanes?: Array<[string, string]>;
  style?: CSSProperties;
  dotColor?: string;
  laneColor?: string;
}

export function USMap({
  stroke = '#1a1a1a',
  fill = 'none',
  dots = [],
  lanes = [],
  style = {},
  dotColor = '#b8351e',
  laneColor = '#0f2a1d',
}: USMapProps) {
  return (
    <svg viewBox="0 0 1000 560" style={{ width: '100%', height: 'auto', display: 'block', ...style }}>
      <path
        d="M120,160 C140,120 200,90 260,90 L340,90 C380,80 420,95 460,100 L560,100 C610,95 660,100 720,95 C780,90 830,95 880,110 C910,120 925,150 920,175 C915,210 880,230 870,260 C865,290 880,310 870,340 C860,370 830,380 800,395 C780,408 770,430 750,445 C720,470 680,475 640,470 C600,465 560,470 520,475 C490,478 470,490 440,488 C400,485 370,470 340,455 C310,440 290,425 260,420 C220,415 190,410 165,395 C140,380 125,355 120,325 C115,295 110,270 110,240 C110,210 110,180 120,160 Z"
        fill={fill} stroke={stroke} strokeWidth="1.5" strokeLinejoin="round"
      />
      <g stroke={stroke} strokeWidth="0.6" opacity="0.25" fill="none">
        <path d="M260,90 L260,420" /><path d="M340,90 L340,455" />
        <path d="M460,100 L460,488" /><path d="M560,100 L560,475" />
        <path d="M680,95 L680,470" /><path d="M780,90 L800,395" />
        <path d="M120,200 L920,200" /><path d="M120,290 L900,290" />
        <path d="M120,380 L860,380" />
      </g>
      {lanes.map((ln, i) => {
        const a = CITY_COORDS[ln[0]];
        const b = CITY_COORDS[ln[1]];
        if (!a || !b) return null;
        const mx = (a[0] + b[0]) / 2;
        const my = (a[1] + b[1]) / 2 - 30;
        return (
          <path key={i} d={`M${a[0]},${a[1]} Q${mx},${my} ${b[0]},${b[1]}`}
            fill="none" stroke={laneColor} strokeWidth="1.5"
            strokeDasharray="4 4" opacity="0.85" />
        );
      })}
      {dots.map((c, i) => {
        const p = CITY_COORDS[c];
        if (!p) return null;
        return (
          <g key={i}>
            <circle cx={p[0]} cy={p[1]} r="8" fill={dotColor} opacity="0.18" />
            <circle cx={p[0]} cy={p[1]} r="3.5" fill={dotColor} />
          </g>
        );
      })}
    </svg>
  );
}

export function PulseDot({ color = '#1F8A5B', size = 8 }: { color?: string; size?: number }) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', width: size, height: size }}>
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%', background: color,
        animation: 'ss-pulse 1.6s ease-out infinite', opacity: 0.45,
      }} />
      <span style={{ position: 'absolute', inset: 0, margin: 'auto', width: size, height: size, borderRadius: '50%', background: color }} />
    </span>
  );
}
